<template>
  <div class="question-bank">
    <div class="query-select">
      <el-form>
        <el-form-item>
          <el-button
            plain
            round
            size="small"
            type="primary"
            @click="openForm('add')"
            >新增题库</el-button
          >
        </el-form-item>
      </el-form>
    </div>
    <div class="question-bank-list">
      <el-row :gutter="20" type="flex" class="question-bank-row">
        <el-col
          :xs="24"
          :sm="12"
          :md="8"
          :lg="6"
          v-for="item in list"
          :key="item.questionBankId || item.id"
          class="question-bank-col"
        >
          <QuestionBankCard
            :data="item"
            :questionBankType="questionBankType"
            @open="openQsForm"
            @success="getList"
          />
        </el-col>
      </el-row>
      <el-pagination
        :key="pagerKey"
        style="margin-top: 15px;"
        @current-change="handlePageChange"
        :current-page.sync="queryParams.pageNum"
        :page-size="queryParams.pageSize || 12"
        layout="total, prev, pager, next"
        :total="totals"
      >
      </el-pagination>
    </div>
    <QuestionBankForm ref="questionBankFormRef" @success="getList" />
    <PracticalForm ref="practicalFormRef" @success="getList" />
    <TheoryForm ref="theoryFormRef" @success="getList" />
  </div>
</template>
<script>
/**
 * 题库管理列表（与管理端一致的三层结构）
 * 1) 本页：questionBankInfoTeacher（questionBankType 区分理论=1 / 实操=2）
 * 2) 进入题库后的题目列表：理论 questionInfoTeacher / 实操 questionPracticalInfoTeacher
 */
import { getQuestionBankList } from "@/api/questionBank";
import QuestionBankForm from "./QuestionBankForm.vue";
import QuestionBankCard from "./QuestionBankCard.vue";
import PracticalForm from "./practical/PracticalForm.vue";
import TheoryForm from "./theory/TheoryBankForm.vue";
import { saveListQuery, getListQuery } from "@/utils/listQueryCache";

function isQuestionParentPath(path) {
  const p = (path || "").split("?")[0];
  return p === "/question/theory" || p === "/question/practical";
}

function isQuestionChildPath(path, parentPath) {
  const p = (path || "").split("?")[0];
  const parent = (parentPath || "").split("?")[0];
  return p.startsWith(parent + "/");
}

export default {
  name: "QuestionBank",
  components: { QuestionBankForm, QuestionBankCard, PracticalForm, TheoryForm },

  data() {
    return {
      questionBankType: 1,
      queryParams: {
        pageNum: 1,
        pageSize: 12
      },
      list: [],
      totals: 0,
      pagerKey: 0,
      pagerSilent: false
    };
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.initListFromRoute(to, from);
    });
  },
  beforeRouteLeave(to, from, next) {
    saveListQuery(this.getCacheKey(), {
      queryParams: { ...this.queryParams },
      questionBankType: this.questionBankType
    });
    next();
  },
  methods: {
    getCacheKey() {
      const path = (this.$route.path || "").split("?")[0];
      return path === "/question/practical"
        ? "/question/practical"
        : "/question/theory";
    },
    applyCachedQueryParams(cachedParams = {}) {
      this.queryParams.pageNum = Number(cachedParams.pageNum) || 1;
      this.queryParams.pageSize = Number(cachedParams.pageSize) || 12;
    },
    async initListFromRoute(to, from) {
      const parentPath = (to.path || "").split("?")[0];
      // 菜单理论/实操 → 题库类型，只请求一次 my-list
      this.questionBankType = parentPath === "/question/theory" ? 1 : 2;

      if (
        from &&
        isQuestionParentPath(parentPath) &&
        isQuestionChildPath(from.path, parentPath)
      ) {
        const cached = getListQuery(parentPath);
        if (cached && cached.queryParams) {
          this.applyCachedQueryParams(cached.queryParams);
        }
      }

      await this.getList({ restorePager: true });
    },
    handlePageChange(val) {
      if (this.pagerSilent) return;
      this.queryParams.pageNum = val;
      this.getList();
    },
    async getList(options = {}) {
      const restorePager = !!(options && options.restorePager);
      const pageNum = Number(this.queryParams.pageNum) || 1;
      this.queryParams.pageNum = pageNum;
      this.pagerSilent = true;
      try {
        const result = await getQuestionBankList({
          pageNum: this.queryParams.pageNum,
          pageSize: this.queryParams.pageSize,
          questionBankType: this.questionBankType
        });
        const data = (result && result.data) || {};
        this.list = data.rows || [];
        this.totals = Number(data.total) || 0;
        this.queryParams.pageNum = pageNum;
        if (restorePager) {
          this.pagerKey += 1;
        }
        saveListQuery(this.getCacheKey(), {
          queryParams: { ...this.queryParams },
          questionBankType: this.questionBankType
        });
        await this.$nextTick();
        this.queryParams.pageNum = pageNum;
      } finally {
        // 延后放开，避免 pagerKey 变更触发二次 current-change
        this.$nextTick(() => {
          setTimeout(() => {
            this.pagerSilent = false;
          }, 0);
        });
      }
    },

    openForm(type, id = null) {
      this.$refs.questionBankFormRef.open(type, id);
    },
    openQsForm(questionBankId) {
      if (this.questionBankType == 1) {
        this.$refs.theoryFormRef.open("create", null, questionBankId);
      } else {
        this.$refs.practicalFormRef.open("create", null, questionBankId);
      }
    }
  }
};
</script>
<style scoped lang="less">
.question-bank {
  &-header {
    position: relative;
    overflow: hidden;
    .el-button {
      float: right;
    }
  }
  &-list {
    margin: 15px 0;
    background: rgba(255, 255, 255, 0.62);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.95),
      0px 2px 8px 0px rgba(0, 0, 0, 0.04),
      0px 8px 32px 0px rgba(99, 102, 241, 0.08);
    padding: 16px;
    color: #4e5969;
  }

  &-row {
    flex-wrap: wrap;
  }

  /deep/ .question-bank-col {
    display: flex;
    margin-bottom: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/QuestionBank/index.vue